import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { SESSION_STORAGE_KEYS } from "@dw/constants";
import { getQuerySettings, QueryKeys } from '@dw/models/shared';
import { cleanJsonResponse } from '@dw/utils';
import { isDayjs } from '@dw/dayjs';

type UpdaterFn<T> = (currentValue: T | null) => T;
type Updater<T> = UpdaterFn<T> | T | undefined;

const isUpdaterFn = <T,>(u: Updater<T>): u is UpdaterFn<T> =>
  typeof u === "function";

export function useSessionStorage<T>(storageKey: SESSION_STORAGE_KEYS, seedValue?: T) {

  const queryClient = useQueryClient();
  const querySettings = useMemo(() => getQuerySettings(), []);

  const initialized = useRef(false);

  const cachedItem = useQuery<T | null>({
    ...querySettings,
    queryKey: [QueryKeys.SessionStorage, storageKey],
    queryFn: () => {
      return new Promise((resolve) => {
        const sessionItem = sessionStorage.getItem(storageKey);
        if (sessionItem && (sessionItem.startsWith('{') || sessionItem.startsWith('['))) {
          resolve(cleanJsonResponse(JSON.parse(sessionItem)) as T);
        } else {
          resolve(cleanJsonResponse(sessionItem) as T);
        }
      });
    },
  });

  const invalidateQueryCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.SessionStorage, storageKey]
    });
  }, [queryClient, storageKey]);

  const updateItem = useCallback((updater: Updater<T>) => {
    const updatedItem = isUpdaterFn(updater)
      ? updater(cachedItem.data ?? null)
      : updater;

    const sessionItem = isDayjs(updatedItem)
      ? updatedItem.toISOString()
      : JSON.stringify(updatedItem);
      
    sessionStorage.setItem(storageKey, sessionItem);
    invalidateQueryCache();
  }, [invalidateQueryCache, storageKey, cachedItem.data]);

  const deleteItem = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    invalidateQueryCache();
  }, [invalidateQueryCache, storageKey]);

  useEffect(() => {
    if (!initialized.current && seedValue) {
      initialized.current = true;
      updateItem(seedValue);
    }
  }, [seedValue, updateItem]);

  return useMemo(() => ({
    value: cachedItem.data ?? null,
    updateItem,
    deleteItem,
    initialized: initialized && cachedItem.isSuccess
  }), [cachedItem, updateItem, deleteItem, initialized]);
}